import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ChevronDown, Check, Star, LayoutDashboard, Users, Briefcase, Home, Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, getTenant } from "../../../lib/auth";

/**
 * Drop-in dashboard switcher used inside every tenant dashboard.
 * - Lists Main + every dashboard belonging to an ACTIVATED module
 * - Lets the user "Set as default" so this dashboard loads on the next login
 */
const ALL_DASHBOARDS = [
  { key: "account", label: "Main Dashboard", icon: LayoutDashboard, path: "dashboard", module: null },
  { key: "crm", label: "CRM Dashboard", icon: Users, path: "dashboard/crm", module: "CRM" },
  { key: "hrm", label: "HRM Dashboard", icon: Briefcase, path: "dashboard/hrm", module: "HR Management" },
  { key: "real-estate", label: "Real Estate Dashboard", icon: Home, path: "dashboard/real-estate", module: "Real Estate" },
  { key: "football-club", label: "Football Club Dashboard", icon: Trophy, path: "dashboard/football-club", module: "Football Club" },
];

export default function DashboardSwitcher({ current = "account" }) {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [defaultKind, setDefaultKind] = useState(null);
  const [saving, setSaving] = useState(false);
  const tenant = getTenant();
  const enabled = new Set(tenant?.modules_enabled || []);
  const hasGating = (tenant?.modules_enabled || []).length > 0;

  useEffect(() => {
    api.get("/t/dashboard-preference")
      .then((r) => setDefaultKind(r.data?.default_dashboard || "account"))
      .catch(() => setDefaultKind("account"));
  }, []);

  // Only show dashboards whose underlying module is activated (or core ones with no module).
  const available = ALL_DASHBOARDS.filter((d) => !d.module || !hasGating || enabled.has(d.module));
  const currentItem = available.find((d) => d.key === current) || available[0];

  const setAsDefault = async (key) => {
    setSaving(true);
    try {
      await api.post("/t/dashboard-preference", { default_dashboard: key });
      setDefaultKind(key);
      toast.success(`${ALL_DASHBOARDS.find((d) => d.key === key)?.label || "Dashboard"} set as your default`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  const goTo = (item) => {
    setOpen(false);
    // Implicit "Set as default": switching to a dashboard makes it the default so it
    // sticks across logout / login until the user picks a different one.
    if (defaultKind !== item.key) {
      setDefaultKind(item.key);
      api.post("/t/dashboard-preference", { default_dashboard: item.key }).catch(() => {
        // Silent — UX shouldn't block on the preference save.
      });
    }
    nav(`/t/${subdomain}/${item.path}`);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest("[data-testid='dashboard-switcher']")) setOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  // Close on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <div className="relative inline-block" data-testid="dashboard-switcher">
      <button onClick={() => setOpen((o) => !o)} data-testid="dashboard-switcher-toggle"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-black/10 hover:border-black/25 text-sm font-bold text-black/85 transition-colors">
        {currentItem?.icon && <currentItem.icon size={15} />}
        <span>{currentItem?.label}</span>
        {defaultKind === current && <Star size={11} className="text-amber-500 fill-amber-400" />}
        <ChevronDown size={13} className={`text-black/55 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1.5 w-72 bg-white border border-black/10 rounded-xl shadow-xl p-1.5">
          {available.map((d) => {
            const isActive = d.key === current;
            const isDefault = defaultKind === d.key;
            const Icon = d.icon;
            return (
              <div key={d.key} className={`group rounded-lg p-2 flex items-center gap-2.5 hover:bg-black/[0.03] ${isActive ? "bg-emerald-50" : ""}`}>
                <button onClick={() => goTo(d)} data-testid={`dashboard-switch-${d.key}`}
                  className="flex-1 flex items-center gap-2.5 text-left min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? "bg-emerald-600 text-white" : "bg-black/[0.04] text-black/65 group-hover:bg-black/[0.08]"}`}>
                    <Icon size={14} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-black truncate flex items-center gap-1">
                      {d.label}
                      {isDefault && <Star size={10} className="text-amber-500 fill-amber-400 flex-shrink-0" />}
                    </div>
                    {isActive && <div className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Viewing</div>}
                    {!isActive && isDefault && <div className="text-[10px] text-amber-700 font-bold uppercase tracking-wider">Your default</div>}
                  </div>
                </button>
                {!isDefault && (
                  <button onClick={() => setAsDefault(d.key)} disabled={saving}
                    data-testid={`dashboard-set-default-${d.key}`}
                    title="Set as default — loads when you log in"
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-amber-50 text-black/40 hover:text-amber-700 disabled:opacity-30">
                    <Star size={13} />
                  </button>
                )}
                {isDefault && <Check size={14} className="text-emerald-600 flex-shrink-0 mr-1.5" />}
              </div>
            );
          })}
          {saving && <div className="px-3 py-2 text-xs text-black/45 flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Saving preference…</div>}
          <div className="border-t border-black/8 mt-1.5 pt-1.5 px-3 py-1.5">
            <p className="text-[10px] text-black/45 leading-snug">Click <Star size={10} className="inline -mt-0.5" /> on a dashboard to make it open by default at sign-in.</p>
          </div>
        </div>
      )}
    </div>
  );
}
